import { Injectable } from '@angular/core';
import { parse, unparse } from 'papaparse';
import { v4 as uuid } from 'uuid';
import { HttpClient } from '@angular/common/http';
import { getUrl, downloadData, uploadData, list, remove, copy } from 'aws-amplify/storage';
import { FileTypeEnum } from 'app/API.service';
import { NetskopeFileEnum } from 'app/shared/enums/netskope-board.enum';
import { NGXLogger } from 'ngx-logger';
import { ObjectsSort } from './helpers/generic-helpers';
import { ToastrService } from 'ngx-toastr';
import * as JSZip from 'jszip';
import { UtilsService } from 'app/shared/utils.service';
import { ActivatedRoute } from '@angular/router';
import {
  FRAMEWORK_RANGES_PATH,
  frameworkRangeMapPath,
  FIRST_PARTY_ENUMS_PATH,
  THIRD_PARTY_ENUMS_PATH,
  FRAMEWORKS_ANSWERS_ENUMS_PATH,
  LICENSE_NOTICE_ENUMS_PATH,
} from 'app/app.paths';
import { FrameworkEnumTypes } from 'app/shared/enums/framework-enum-types.enum';
import { ClientLambdaService } from './client-lambda.service';
// npm install pdfjs-dist@4.10.38
// import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import * as PDFJS from 'pdfjs-dist/build/pdf.min.mjs';
import { Observable } from 'rxjs';
import * as ExcelJS from 'exceljs';
import { CUSTOMAPIService } from 'app/custom-api.service';

export interface IstandardsNIndustries {
  category: string;
  standards_name: string;
  industry: string;
  comments: string;
}

export interface StoragePutResponse {
  key: string;
}

@Injectable()
export class FileService {
  static fileSize = 31457280; // 30MB
  static logoFileSizeLimit = 2097152; // 2MB

  encryptedFolders = ['ASSESSMENTS/', 'public/ASSESSMENTS/'];

  frameworkRangeMap = null;
  private frameworkRangeDictionary = {}; // Store the Data in key value Pair ( To make the Searching Fast in O(n) )
  StandardEnum: null;
  RiskFrameworkEnum: null;
  VendorRiskFrameworkEnum: null;
  CERTIFICATIONS: null; // these  Enums used for Vendor Compliance Frameworks
  isAllEnumsLoaded = false;
  FrameworksAnswersEnum = null;
  private licenseNoticeEnum = null;
  private maliciousPatterns = [
    /<script.*?>.*?<\/script>/gi, // Detect <script> tags
    /exec\(.*?\)/gi, // Detect exec() function calls
    /system\(.+?\)/gi, // Detect system() function calls
    /\/Type\s*\/Action\s*\/S\s*\/JavaScript/gi, // Detect JavaScript actions
    /app\.trustedFunction\s*=\s*function/gi, // Detect potential JavaScript-based attacks
    // eslint-disable-next-line max-len
    /(?:\bvar\b|\bconst\b)[\s\S]*?(?:[;{}]|(?=<\/?\w+>))/g, // Detect any type of javascript code in the pdf file
  ];

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private toastrService: ToastrService,
    private route: ActivatedRoute,
    private clientLambdaService: ClientLambdaService,
    private customApi: CUSTOMAPIService
  ) {
    this.init();
  }

  async init(): Promise<boolean> {
    try {
      const promises = [];
      // step 1 : get Framework Ranges
      promises.push(this.getFrameworkRangeMap());
      // step 2 : get First party framework Enums
      promises.push(this.getFirstPartyEnums());
      // step 3 : get Third Party Framework Enums
      promises.push(this.getThirdPartyEnums());
      // step 4: get Framework Answers Enums
      promises.push(this.getFrameworksAnswersEnums());
      // step 5: get License Notice Enum
      promises.push(this.getLicenseNoticeEnum());

      await Promise.all(promises);

      this.isAllEnumsLoaded = true;
      return true;
    } catch (e) {
      console.log('Error : ');
    }
  }

  /**
   * Get All Global Framework Ranges.
   */
  async getFrameworkRangeMap(): Promise<any[]> {
    try {
      if (!this.frameworkRangeMap) {
        const responseData = await this.getFileByPathFromS3(frameworkRangeMapPath);
        if (responseData) {
          this.frameworkRangeMap = responseData.FrameworkRangeMap;
          this.buildFrameworkRangesDictionary();
        }
      }
      return this.frameworkRangeMap;
    } catch (e) {
      console.log('Error : ', e);
    }
    return [];
  }
  /**
   * Create the Dictionary from Framework Ranges map list to keep data in key value pair.
   * Make the Searching FAST. It will take O(1) to get value instead of getting value in O(n)
   */
  private buildFrameworkRangesDictionary(): void {
    if (this.frameworkRangeMap) {
      this.frameworkRangeMap.forEach(framework => {
        this.frameworkRangeDictionary[framework.name] = framework;
      });
    }
  }
  /**
   * @param frameworkKey framework name as key
   * @returns framework with left and Right Ranges
   */
  getFrameworkWithRange(frameworkKey: string): any {
    let frameKey = frameworkKey;
    if (frameKey.includes('#')) {
      frameKey = frameKey.split('#')[0];
    }
    return this.frameworkRangeDictionary[frameKey];
  }

  /**
   * @param id will be either rootEntityId,childEntityId or vendorId
   * @returns custom Frameworks Ranges
   */
  async getCustomFrameworkRangeMap(id: string): Promise<any[]> {
    try {
      const responseData = await this.getFileByPathFromS3(FRAMEWORK_RANGES_PATH + id + '.json');
      return responseData.FrameworkRangeMap ? responseData.FrameworkRangeMap : [];
    } catch (e) {
      console.log('Error : ', e);
    }
    return [];
  }
  /**
   * get the Cashed Enums that shared in s3 instead of local
   * @param frameworkEnumType is type of FrameworkEnumTypes
   * @returns Enum Object
   */
  importFrameworkEnumsFromS3(frameworkEnumType: FrameworkEnumTypes): any {
    if (this.isAllEnumsLoaded) {
      let enumData = null;
      switch (frameworkEnumType) {
        case FrameworkEnumTypes.RISK_FRAMEWORK_ENUM:
          enumData = this.RiskFrameworkEnum;
          break;
        case FrameworkEnumTypes.STANDARD_ENUM:
          enumData = this.StandardEnum;
          break;
        case FrameworkEnumTypes.VENDOR_RISK_FRAMEWORK_ENUM:
          enumData = this.VendorRiskFrameworkEnum;
          break;
        case FrameworkEnumTypes.CERTIFICATIONS_ENUM:
          enumData = this.CERTIFICATIONS;
          break;
        case FrameworkEnumTypes.FRAMEWORKS_ANSWERS_ENUMS:
          enumData = this.FrameworksAnswersEnum;
          break;
        case FrameworkEnumTypes.LICENSE_NOTICE_ENUM:
          enumData = this.licenseNoticeEnum;
          break;
      }
      return enumData;
    } else {
      console.log('Framework Enums Fail to load from s3 ');
    }
    return null;
  }

  /**
   * get StandardEnum and RiskFrameworkEnum Object from s3( Risk & Compliance Framework )
   * @returns an object of Standards
   */
  async getFirstPartyEnums(): Promise<void> {
    try {
      if (!this.StandardEnum || !this.RiskFrameworkEnum) {
        const responseData = await this.getFileByPathFromS3(FIRST_PARTY_ENUMS_PATH);
        if (responseData) {
          this.StandardEnum = responseData.StandardEnum;
          this.RiskFrameworkEnum = responseData.RiskFrameworkEnum;
        }
      }
    } catch (e) {
      console.log('S3->FrameworkEnums : ', e);
    }
  }

  /**
   * get CERTIFICATIONS and VendorRiskFrameworkEnum Object from s3( Risk & Compliance Framework )
   * @returns an object of Standards
   */
  async getThirdPartyEnums(): Promise<void> {
    try {
      if (!this.VendorRiskFrameworkEnum || !this.CERTIFICATIONS) {
        const responseData = await this.getFileByPathFromS3(THIRD_PARTY_ENUMS_PATH);
        if (responseData) {
          this.VendorRiskFrameworkEnum = responseData.VendorRiskFrameworkEnum;
          this.CERTIFICATIONS = responseData.CERTIFICATIONS;
        }
      }
    } catch (e) {
      console.log('S3->FrameworkEnums : ', e);
    }
  }

  /**
   * get framework answer enums Object from s3
   * @returns an object of AnswerEnum
   */
  async getFrameworksAnswersEnums(): Promise<void> {
    try {
      if (!this.FrameworksAnswersEnum) {
        const responseData = await this.getFileByPathFromS3(FRAMEWORKS_ANSWERS_ENUMS_PATH);
        if (responseData) {
          this.FrameworksAnswersEnum = responseData;
        }
      }
    } catch (e) {
      console.log('S3->FrameworkEnums : ', e);
    }
  }

  /**
   * get license notice enum Object from s3
   * @returns an object of LicenseNoticeEnum
   */
  async getLicenseNoticeEnum(): Promise<void> {
    try {
      if (!this.licenseNoticeEnum) {
        const responseData = await this.getFileByPathFromS3(LICENSE_NOTICE_ENUMS_PATH);
        if (responseData) {
          this.licenseNoticeEnum = responseData;
        }
      }
    } catch (e) {
      console.error('S3->LicenseNoticeEnum : ', e);
    }
  }

  /**
   * function to check whether the framework is licensed or not
   * @param frameworkKey string
   * @returns boolean
   */
  public isLicensedFramework(frameworkKey: string): boolean {
    const licenseNoticeEnum = this.importFrameworkEnumsFromS3(FrameworkEnumTypes.LICENSE_NOTICE_ENUM) || [];
    if (licenseNoticeEnum && licenseNoticeEnum.includes(frameworkKey)) {
      return true;
    }
    return false;
  }

  static validateFileSize(file: File): boolean {
    return file.size > FileService.fileSize;
  }

  static validateFileType(file: File, fileType: string): boolean {
    // verification for FileTypeEnum
    const fileExt = file.name.match(/\.([^.]+)$/);
    const ext = fileExt ? fileExt[1].toLowerCase() : '';
    const allowImgTypes = ['jpg', 'jpeg', 'bmp', 'png', 'tif'];
    const allowArtifactsTypes = ['doc', 'docx', 'csv', 'pdf', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];
    //* Mid market File types
    const midMarketAllowedFileTypes = ['doc', 'docx', 'pdf', 'txt', 'jpg', 'jpeg', 'png'];
    let validType = false;
    switch (fileType) {
      case FileTypeEnum.LOGO:
        if (allowImgTypes.includes(ext)) {
          validType = true;
        }
        break;
      case FileTypeEnum.ARTIFACTS:
      case FileTypeEnum.MANUAL_ARTIFACT:
      case FileTypeEnum.VENDOR_ARTIFACTS_TEMPLATE:
        if (allowImgTypes.includes(ext) || allowArtifactsTypes.includes(ext)) {
          validType = true;
        }
        break;
      case FileTypeEnum.MIDMARKET_FILES:
        if (midMarketAllowedFileTypes.includes(ext)) {
          validType = true;
        }
        break;
    }

    return validType;
  }

  async uploadToS3(s3File: any, isDefaultToaster = true): Promise<any> {
    if (!s3File) {
      return;
    }

    if (s3File.fileType !== FileTypeEnum.ARTIFACTS) {
      s3File.name = s3File?.name?.replace(/\s/g, '');
    }

    let path = '';
    const prefix = 'public'; // replicating the `level: public` usage

    switch (s3File.fileType) {
      case FileTypeEnum.LOGO:
        path = `${prefix}/CLIENTS/${s3File.entityId}/${s3File.fileType}/${s3File.name}`;
        break;

      case FileTypeEnum.ARTIFACTS:
        s3File.id = uuid();
        path = `${prefix}/CLIENTS/${s3File.entityId}/${s3File.fileType}/${s3File.id}`;
        break;

      case 'HITRUST':
        path = `${prefix}/ASSESSMENTS/${s3File.assessmentId}/hitrust-verification/${s3File.name}`;
        s3File.body = JSON.stringify(s3File);
        break;

      case FileTypeEnum.BOARD_REPORTS:
        path = `${prefix}/${FileTypeEnum.BOARD_REPORTS}/${s3File.entityId}/${s3File.id}.zip`;
        break;

      case FileTypeEnum.VENDOR_ARTIFACTS_TEMPLATE:
        s3File.id = uuid();
        path = `${prefix}/${FileTypeEnum.VENDOR_ARTIFACTS_TEMPLATE}/${s3File.entityId}/${s3File.name}/${s3File.id}`;
        break;
      case 'POLICY_FILES':
        path = `${prefix}/POLICY_MANAGEMENT/POLICIES/${s3File.rootEntityId}/${s3File.policyId}/${s3File.name}`;
        break;
      case 'POLICY_TEMPLATES':
        path = `${prefix}/POLICY_MANAGEMENT/POLICY_TEMPLATES/${s3File.rootEntityId}/${s3File.policyTemplateID}/${s3File.name}`;
        break;
      case 'SUPPORT_FILES':
        s3File.id = uuid();
        path = `${prefix}/SUPPORT_FILES/${s3File.assessmentId}/${s3File.assessmentId}.zip`;
        break;

      case NetskopeFileEnum.NETSKOPE_MATURITY_DATA:
        path = `${prefix}/NETSKOPE_JIRA/SAVED_JSON_DATA/${s3File.id}/MATURITY.json`;
        s3File.body = JSON.stringify(s3File);
        break;

      case NetskopeFileEnum.NETSKOPE_OVERVIEW_DATA:
        path = `${prefix}/NETSKOPE_JIRA/SAVED_JSON_DATA/${s3File.id}/OVERVIEW.json`;
        s3File.body = JSON.stringify(s3File);
        break;

      case FileTypeEnum.CLIENT_WIZARD_FILES:
      case FileTypeEnum.E_SIGNATURE:
      case FileTypeEnum.ESE_TEMPLATES:
        path = `${prefix}/${s3File.url}`; // these are already the final S3 path
        break;

      case FileTypeEnum.REVISIONS:
        return; // If nothing is needed for REVISIONS, skip upload

      default:
        throw new Error(`Unsupported file type: ${s3File.fileType}`);
    }

    try {
      if (isDefaultToaster) {
        this.toastrService.info(
          `Your file(s) is being uploaded in the background. This process can take several minutes. 
            Please do not close or refresh the browser.`
        );
      }

      // >>> USE PRESIGNED URL INSTEAD OF S3 SDK FUNCTION
      // 1. Get a presigned url for this path and content type from backend/custom api
      //    Assume there is a function `getPresignedS3UploadUrl` available on this service or injected service
      //    Example: const { url, fields } = await this.getPresignedS3UploadUrl(path, s3File.contentType);

      // Replace the below with your own backend call to get the correct upload URL
      // This is a placeholder method, adjust as needed for your backend.
      const { url: presignedUrl, fields } = await this.getPresignedS3UploadUrl(path, s3File.contentType);

      // Prepare the upload body. If "fields" exist, it's a multipart/form upload (common for S3),
      // otherwise, just use PUT (common for simple presigned urls).
      let uploadResponse;
      if (fields) {
        // Multipart/form-data upload
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          formData.append(key, value as string);
        });
        formData.append('file', s3File.body);

        uploadResponse = await fetch(presignedUrl, {
          method: 'POST',
          body: formData,
        });
      } else {
        // PUT upload
        uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          body: s3File.body,
          headers: {
            'Content-Type': s3File.contentType || 'application/octet-stream',
          },
        });
      }

      if (!uploadResponse.ok) {
        throw new Error('S3 upload via presigned URL failed.');
      }

      s3File.key = path;
      delete s3File.body;

      if (isDefaultToaster) {
        this.toastrService.success('The file(s) has been successfully uploaded.');
      }

      return s3File;
    } catch (err) {
      this.logger.error('uploadToS3 - Error: ', err);
      this.toastrService.error('Internal server error while uploading file(s).');
      throw err;
    }
  }

  async downloadFromS3(s3File: any): Promise<any | string> {
    delete s3File.__typename;
    const config: any = { contentType: s3File.contentType, level: 'public' };
    const key = s3File?.key?.includes('public/') ? s3File.key : `public/${s3File.key}`;
    return (await getUrl({ path: key })).url;
  }

  s2ab(s: string): ArrayBuffer {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) {
      // eslint-disable-next-line no-bitwise
      view[i] = s.charCodeAt(i) & 0xff;
    }
    return buf;
  }

  async JSONToExcelConversion(json) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('TEMPLATE');

    if (json.length > 0) {
      // Add header row
      worksheet.columns = Object.keys(json[0]).map(key => ({
        header: key,
        key,
        width: 20,
      }));

      // Add data rows
      json.forEach(item => worksheet.addRow(item));
    }

    // Write workbook to buffer
    const buffer = await workbook.xlsx.writeBuffer();

    // Create Blob for download
    return new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  async downloadFileFromS3(path: string): Promise<any | string> {
    const key = path.includes('public/') ? path : `public/${path}`;
    // const s3URL = await getUrl({ path: key });
    // return s3URL.url.href;
    const res: any = await this.customApi.s3Handler(key);
    const url = await res.body.json();
    return url.data?.path;
  }

  async deleteFromS3(s3File: any, keyName = 'key'): Promise<any> {
    // const config: any = { level: 'public' };
    const key = s3File[keyName]?.includes('public/') ? s3File[keyName] : `public/${s3File[keyName]}`;
    // return (await remove({ path: key }))?.path;
    return await this.customApi.s3Handler(key, '', 'delete');
  }

  /**
   * Delete multiple files at a folder/directory from S3
   * @param basicPath contain path of a folder
   * @param filesNames names of files to delete with file extension
   * @returns deleted files status
   */
  async deleteMultipleFilesFromS3(basicPath: string, filesNames: any[], isZip = false): Promise<any> {
    try {
      if (basicPath && filesNames?.length) {
        const promises = [];
        filesNames.map(name => {
          if (!isZip) {
            promises.push(this.deleteFromS3({ key: basicPath + name }));
          } else {
            promises.push(this.deleteFromS3({ key: basicPath + name + '.zip' }));
          }
        });
        return await Promise.all(promises);
      }
    } catch (e) {
      console.log('Error : ', e);
    }
    return null;
  }

  getBase64(file: any): Promise<any | string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  getImgFileFromBase64(dataurl: any, filename): File {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  parseFile(csv): Promise<any> {
    return new Promise((resolve, reject) => {
      parse(csv, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        delimiter: ',',
        encoding: 'UTF-8',
        complete(results, file) {
          // console.log(`file => ${file}`);
          // console.clear();
          resolve(results);
        },
        error(err) {
          this.logger.error(err);
          reject(err);
        },
      });
    });
  }
  // This is synchronous function hence no Promise required
  unParseFile(json): string {
    return unparse(json, {
      header: true,
      delimiter: ',',
    });
  }

  getFile(filePath: string, options: any = { responseType: 'text' }): Promise<any> {
    return this.http.get(filePath, { ...options }).toPromise();
  }

  getJsonData(jsonUrl): Observable<any> {
    return this.http.get<any>(jsonUrl);
  }

  async uploadToS3CustomPath(path: string, body: any, config: any): Promise<any> {
    try {
      path = path.includes('public/') ? path : `public/${path}`;
      const data = path.includes('.json') ? JSON.stringify(body) : body;
      const res: any = await this.getPresignedS3UploadUrl(path, config.contentType);
      return await fetch(res.url, {
        method: 'PUT',
        body: data,
        headers: {
          'Content-Type': config.contentType,
        },
      });
    } catch (e) {
      this.logger.error('uploadToS3 - Error: ', e);
      return Promise.reject(e);
    }
  }

  async getUploadedImg(e: Event): Promise<any> {
    return new Promise((res, rej) => {
      const eventTarget: HTMLInputElement = e.target as HTMLInputElement;
      const imageFile = e && eventTarget.files[0] ? eventTarget.files[0] : null;
      if (!imageFile) {
        res(null);
      }
      try {
        res(this.getBase64(imageFile));
      } catch (error) {
        rej(error);
      }
    });
  }

  verifyUploadFile(e: any, fileType: string): Promise<boolean> {
    return new Promise((res, rej) => {
      const eventTarget: any = (e?.target as HTMLInputElement) || (e as unknown as FileList[]);
      const file: any =
        eventTarget.files && eventTarget?.files[0]
          ? eventTarget?.files[0]
          : eventTarget.length && eventTarget[0]
            ? eventTarget[0]
            : eventTarget
              ? eventTarget
              : null;
      let errMsg = null;
      // verification check for all files
      if (!file) {
        errMsg = 'An occurred while uploading file';
        rej(errMsg);
      }
      const matchFile = file.name.match(/\.([^.]+)$/);

      if (!matchFile) {
        res(false);
      }
      // verification for FileTypeEnum
      const ext = matchFile.length > 1 ? matchFile[1]?.toLowerCase() : null;
      const allowImgTypes = ['jpg', 'jpeg', 'bmp', 'png', 'tif', 'gif'];
      const allowArtifactsTypes = ['doc', 'docx', 'csv', 'pdf', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'];
      let validType = false;
      switch (fileType) {
        case FileTypeEnum.LOGO:
          if (file?.size > FileService.logoFileSizeLimit) {
            errMsg = 'Max file size for upload 2MB';
          } else if (!allowImgTypes.includes(ext)) {
            errMsg = 'File type not supported';
          } else {
            validType = true;
          }
          break;
        case FileTypeEnum.ARTIFACTS:
        case FileTypeEnum.MANUAL_ARTIFACT:
        case 'POLICY_FILES':
        case FileTypeEnum.VENDOR_ARTIFACTS_TEMPLATE:
          if (file?.size > FileService.fileSize) {
            errMsg = 'Max file size for upload 30MB';
          } else if (!allowArtifactsTypes.includes(ext) && !allowImgTypes.includes(ext)) {
            errMsg = 'File type not supported';
          } else {
            validType = true;
          }
          break;
        default:
          errMsg = 'File type not supported';
      }
      if (validType && !errMsg) {
        res(true);
      } else {
        rej(errMsg);
      }
    });
  }

  async deleteFolderFromS3(folderPrefix: string): Promise<void> {
    const accessLevel = 'private'; // or 'protected' / 'guest'
    const normalizedPrefix = folderPrefix; // can include 'public/' as part of key

    try {
      const listed = await list({
        prefix: normalizedPrefix, // ✅ NEW API expects `prefix`
        options: {
          accessLevel,
          listAll: true, // ✅ required for full folder listings
        },
      });

      const files = listed?.items ?? [];

      if (files.length === 0) {
        return;
      }

      const deletePromises = files.map(file =>
        remove({
          key: file.key, // ✅ `key` instead of `path`
          options: { accessLevel },
        })
      );

      await Promise.all(deletePromises);
      console.log(`Deleted ${files.length} file(s) from folder: ${normalizedPrefix}`);
    } catch (error) {
      console.error('Error deleting folder contents:', error);
      throw error;
    }
  }

  async convertToS3Input(e: Event, entityId): Promise<any> {
    return new Promise((res, rej) => {
      try {
        const eventTarget: HTMLInputElement = e?.target as HTMLInputElement;
        const imageFile: any = e && eventTarget?.files[0] ? eventTarget?.files[0] : null;

        const s3File = {
          name: imageFile?.name,
          entityId,
          fileType: FileTypeEnum?.LOGO,
          contentType: imageFile?.type,
          body: imageFile,
        };

        res(s3File);
      } catch (error) {
        this.logger.error('Image Handler  Error =', error);
        rej('An occurred while uploading logo');
      }
    });
  }

  async getFileUrlFromBucket(path, options): Promise<any> {
    const key = path.includes('public/') ? path : `public/${path}`;
    return (await getUrl({ path: key })).url.href;
  }

  convertCSVDataToJSON(csvDataAsText): IstandardsNIndustries[] {
    const arr = csvDataAsText.split('\n');
    let headers;
    let result = [];
    for (let i = 0; i < arr.length; i++) {
      if (i === 0) {
        headers = arr[i].split(',');
      } else {
        const rowData = arr[i].split(',');
        const object = {};
        headers.map((header, indx) => {
          const key = header.toLowerCase().trim().replace(/\s/g, '_'); // key => Standards Name => standards_name
          object[key] = rowData[indx];
        });
        result.push(object);
      }
    }
    // Sort by Industry column
    if (result.length > 0) {
      result = ObjectsSort(result, 'industry');
    }
    return result;
  }

  async checkIfPathExistsS3(path: string): Promise<any> {
    path = path.includes('public/') ? path : `public/${path}`;
    const res = await list({ path });
    if (res?.items?.length) {
      return res.items;
    }
    return res;
  }

  // <============== B & B Feature ==============>

  async createBnbReport(parentId: string, reportLevel): Promise<string> {
    const json = {
      args: {
        parentId,
        reportLevel,
      },
    };
    try {
      const { Payload } = await this.clientLambdaService.invokeLambda('bnbGenerateReport', { ...json });
      const response = JSON.parse(Payload.transformToString());
      if (response.statusCode === 200) {
        const body = JSON.parse(response.body).data;
        this.toastrService.success('Report generated successfully!');
        const s3File = await this.getPresignedS3DownloadUrl(body.downloadUrl);
        if (s3File) {
          this.toastrService.success('Report downloaded successfully!');
          return s3File;
        }
      } else {
        this.toastrService.error('Report creation failed!');
        console.log('Report creation failed, serverside error ' + JSON.stringify(response.error));
        return '';
      }
    } catch (error) {
      this.toastrService.error('Issue while generating data');
      console.log('Error while Generating Entity Data frontend ' + JSON.stringify(error));
    }
  }

  // Download BnB report
  async downloadBnbReport(reportId): Promise<string | null> {
    if (reportId) {
      const filePath = `${FileTypeEnum.BnB_REPORTS}/${reportId}.pdf`;
      return await this.downloadFileFromS3(filePath);
    } else {
      return null;
    }
  }

  async getFrameworkRangeMapWithId(id: string): Promise<any[]> {
    try {
      const path = id === 'default_ranges' ? 'GLOBAL_OBJECTS/default_ranges.json' : `FRAMEWORK_RANGES/${id}.json`;
      const responseData = await this.getFileByPathFromS3(path);
      return responseData?.FrameworkRangeMap ? responseData.FrameworkRangeMap : [];
    } catch (e) {
      console.log('Error : ', e);
    }
    return [];
  }

  async getZipFileFromS3(filePath, cached = false) {
    try {
      const s3File = await this.getPresignedS3DownloadUrl(filePath);
      const zip = new JSZip();
      const originalFile = await zip.loadAsync(s3File.Body);
      const fileName = Object.keys(originalFile.files)[0];
      const blob = await originalFile.files[fileName].async('blob');
      const textContent = await blob.text();
      return JSON.parse(textContent);
    } catch (err) {
      console.log('getZipFileFromS3 err - ', err);
    }
  }

  async getFileByPathFromS3(filePath: string): Promise<any> {
    try {
      const key = filePath.includes('public/') ? filePath : `public/${filePath}`;
      const res: any = await this.customApi.s3Handler(key);
      const url = await res.body.json();
      const response: any = await fetch(url.data?.path);
      const responseText = await response.text();
      return JSON.parse(responseText);
    } catch (err) {
      return null;
    }
  }

  /**
   * This function will be triggered if the user uploads a template for Artifacts
   * @param {any} eventFile this list will contain the file to upload
   */
  async uploadZippedFiles(eventFile: any, fileType: any, fileObj: any = null): Promise<any> {
    try {
      const fileEvent = fileObj?.templateURL ? fileObj.templateURL : eventFile;
      await this.verifyUploadFile(fileEvent, fileType);

      if (eventFile) {
        const entityId = UtilsService.getRouteParam(this.route.root.snapshot, 'entityId');
        const zipped = new JSZip().file(fileEvent.name, fileEvent);
        const zip = (await zipped.generateAsync({ type: 'blob' })) as any;
        const s3Object: any = {
          name: fileEvent.name,
          entityId,
          fileType,
          contentType: zip.type,
          body: zip,
          date: Date.now(),
        };
        const uploadedObject = await this.uploadToS3(s3Object);
        return uploadedObject;
      }
    } catch (e) {
      console.log('Cannot upload the file ------', e);
      Promise.reject();
    }
  }

  /**
   * Function will invoke the lambda for the creation of the custom framework.
   * @param payload is the data to be passed to the lambda.
   */
  async creatCustomFramework(payload): Promise<void> {
    try {
      // Invoking lambda
      await this.clientLambdaService.invokeLambda('createVendorEntity', payload, 'Event');
    } catch (e) {
      console.log(' Error : ', e);
    }
  }

  async getJSONFileFromS3(filePath: string, options: any): Promise<any> {
    if (!!this.encryptedFolders.find(fName => filePath.startsWith(fName)) && !filePath.includes('.zip')) {
      const jsonText = await this.getDecryptedFile(filePath);
      return jsonText ? JSON.parse(jsonText) : null;
    } else {
      // const s3Data = await downloadData({ path: filePath }).result;
      // return await s3Data.body.text();
      const res: any = await this.customApi.s3Handler(filePath);
      const url = await res.body.json();
      const response: any = await fetch(url.data?.path);
      return await response.text();
    }
  }

  // async getTextFileFromS3(filePath: string, options: any): Promise<string> {
  //   if (!!this.encryptedFolders.find(fName => filePath.startsWith(fName)) && !filePath.includes('.zip')) {
  //     const jsonText = await this.getDecryptedFile(filePath);
  //     return jsonText;
  //   } else {
  //     filePath = filePath.includes('public') ? filePath : 'public/' + filePath;
  //     const s3File = await downloadData({ path: filePath }).result;

  //     console.log({ s3File });
  //     return await s3File.body.text();
  //   }
  // }

  async getTextFileFromS3(filePath: string, options: any): Promise<any> {
    filePath = filePath.includes('public') ? filePath : 'public/' + filePath;

    if (!!this.encryptedFolders.find(fName => filePath.startsWith(fName)) && !filePath.includes('.zip')) {
      const jsonText = await this.getDecryptedFile(filePath);
      return jsonText ? JSON.parse(jsonText) : null;
    }

    // Use customApi.s3Handler flow instead of direct downloadData
    const res: any = await this.customApi.s3Handler(filePath);
    const url = await res.body.json();
    const response: any = await fetch(url.data?.path);

    if (filePath.endsWith('.xlsx')) {
      const arrayBuffer = await response.arrayBuffer();
      return arrayBuffer;
    } else if (filePath.endsWith('.csv')) {
      const csvText = await response.text();
      const parsed = parse(csvText, {
        header: true, // Set to false if CSV has no header row
        skipEmptyLines: true,
      });
      return parsed.data;
    } else {
      const text = await response.text();
      return JSON.parse(text);
    }
  }

  async getDecryptedFile(path: string, entityId: string = null): Promise<any> {
    path = path.includes('public') ? path : 'public/' + path;

    if (!entityId) {
      // get from url
      entityId = UtilsService.getRouteParam(this.route.root.snapshot, 'entityId');
    }

    if (!entityId) {
      console.log('entityId is required to get file: ', path);
      return;
    }

    const response: any = await this.clientLambdaService.invokeLambda('securityHandler', {
      action: 'read_encrypted_file',
      params: {
        rootId: entityId,
        path,
      },
    });

    const compressed64 = JSON.parse(response?.Payload?.transformToString());
    const compressedBffr = Buffer.from(compressed64.data);
    const blob1 = new Blob([compressedBffr]);

    const zip = new JSZip();
    const originalFile = await zip.loadAsync(blob1);
    const fileName = Object.keys(originalFile.files)[0];
    const blob = await originalFile.files[fileName].async('blob');
    const textContent = await blob.text();
    return textContent;
  }

  getFrameworkAnswersOptions(frameworkName: string): any {
    let keyName = null;
    const FRAMEWORKS_ANSWERS = this.FrameworksAnswersEnum;
    const keys = Object.keys(FRAMEWORKS_ANSWERS);
    const name = this.removeStringAfterHash(frameworkName);
    for (const key of keys) {
      if (key === name) {
        keyName = key;
      }
    }
    if (keyName) {
      return { ...FRAMEWORKS_ANSWERS[keyName] };
    }
    if (UtilsService.isBnB) {
      return { ...FRAMEWORKS_ANSWERS.BNB_FRAMEWORKS };
    }
    //* For MidMarket and CyberSite
    //* Separated the framework Answers for MidMarket and CyberSite
    if (UtilsService.isBnBCyberSite || UtilsService.isMidMarket) {
      return { ...FRAMEWORKS_ANSWERS.BEECHER_MIDMARKET_FRAMEWORKS };
    }
    return { ...FRAMEWORKS_ANSWERS.OTHERS_FRAMEWORKS };
  }

  private removeStringAfterHash(inputString) {
    const index = inputString.indexOf('#');
    if (index !== -1) {
      return inputString.substring(0, index);
    }
    return inputString;
  }

  async scanPdfFile(file): Promise<boolean> {
    let isMalicious = false;
    try {
      PDFJS.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs';
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      const typedArray = new Uint8Array(arrayBuffer);
      const pdf = await PDFJS.getDocument(typedArray).promise;
      const pageCount = pdf.numPages;
      for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item: any) => item.str).join(' ');
        // Iterate through patterns and check if any matches the content
        for (const pattern of this.maliciousPatterns) {
          if (pattern.test(pageText)) {
            isMalicious = true;
            break; // Malicious content detected
          }
        }
        // Break the loop if malicious content is found
        if (isMalicious) {
          break;
        }
      }
      return isMalicious;
    } catch (err) {
      if (err?.message === 'No password given') {
        this.toastrService.info('File is password protected so uploading it anyway.');
      }
      return isMalicious;
    }
  }

  private async readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: any) => {
        resolve(event.target.result);
      };
      reader.onerror = event => {
        reject(event);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Copies a file in S3 from source to destination using presigned URLs via s3Handler
   */
  async copyFile(source: string, destination: string): Promise<any> {
    try {
      // 1. Get presigned download URL for source
      const sourceDownloadUrl = await this.getPresignedS3DownloadUrl(source);
      if (!sourceDownloadUrl) {
        throw new Error('Failed to get presigned download URL for source file');
      }

      // 2. Download source file as a Blob
      const downloadRes = await fetch(sourceDownloadUrl);
      if (!downloadRes.ok) {
        throw new Error('Failed to download source file from S3');
      }
      const blob = await downloadRes.blob();

      // 3. Get presigned upload URL for destination
      // Try to determine content type from the downloaded blob
      const contentType = blob.type || 'application/octet-stream';
      const presignedUpload = await this.getPresignedS3UploadUrl(destination, contentType);
      if (!presignedUpload?.url) {
        throw new Error('Failed to get presigned S3 upload URL for copy destination');
      }

      // 4. Upload Blob to destination using presigned URL
      let uploadRes;
      if (presignedUpload.fields) {
        // Multipart/form-data upload (S3 POST policy)
        const formData = new FormData();
        Object.entries(presignedUpload.fields).forEach(([key, value]) => {
          formData.append(key, value as string);
        });
        formData.append('file', blob);
        uploadRes = await fetch(presignedUpload.url, {
          method: 'POST',
          body: formData,
        });
      } else {
        // Simple PUT upload
        uploadRes = await fetch(presignedUpload.url, {
          method: 'PUT',
          body: blob,
          headers: {
            'Content-Type': contentType,
          },
        });
      }

      if (!uploadRes.ok) {
        throw new Error('Failed to upload file to S3 during copy');
      }

      return {
        success: true,
        destination,
        status: uploadRes.status,
      };
    } catch (error) {
      console.error('Error copying file from S3:', error);
      throw error;
    }
  }

  async downloadFilesFromS3(key: string): Promise<any> {
    try {
      const sourceDownloadUrl = await this.getPresignedS3DownloadUrl(key);
      const downloadRes = await fetch(sourceDownloadUrl);
      if (!downloadRes.ok) {
        throw new Error('Failed to download source file from S3');
      }
      const blob = await downloadRes.blob();

      return {
        blob,
      };
    } catch (error) {
      console.error(`Error downloading ${key}:`, error);
      throw error;
    }
  }

  async getPresignedS3UploadUrl(path: string, contentType: string): Promise<any> {
    const key = path?.includes('public/') ? path : `public/${path}`;
    const res: any = await this.customApi.s3Handler(key, contentType, 'put');
    const s3Response = await res.body.json();
    return { url: s3Response.data?.path, fields: s3Response?.data?.fields };
  }

  async getPresignedS3DownloadUrl(path: string): Promise<any> {
    const key = path?.includes('public/') ? path : `public/${path}`;
    const res: any = await this.customApi.s3Handler(key, 'get');
    const s3Response = await res.body.json();
    return s3Response.data?.path;
  }
}
